// custom-sample-editor.js
//
// Editor for the $65 Custom Wallpaper Sample product. Simplified sibling of
// wallpaper-configurator.js: one image, one fixed 24"x24" sample area, one
// $65 variant. Reuses the upload validation / HEIC / DNG / PDF / TIFF / S3
// pieces from the full configurator (copied, not imported, so this file has
// no dependency on the four-controller event-bus architecture there).
//
// Crop UX (Ross, 2026-09-04): the photo pans under a LOCKED wall-shaped frame
// (exactly like mural mode) and a 24"x24" square — sized as a fraction of
// that frame equal to 24 / wallWidthInches — drags inside it. The two edit
// modes are sequential steps, not a free toggle: Step 3 ("position") pans the
// photo with the square hidden; "Apply Wall Crop" locks the photo and opens
// Step 4 ("sample"), where only the square drags. Changing the photo or the
// wall size drops back to Step 3. Cropper's own .cropper-modal greys
// everything outside the wall frame; a second overlay
// (.cse-sample-shade/.cse-sample-box) greys everything inside the frame
// except the square.
//
// Line-item properties match the exact key spellings make_order_previews.py
// and make_custom_samples.py read (see docs/shopify-theme/claude-dev-cli
// notes in the implementation plan): _Crop_Data / _Wall_Crop_Data are sent as
// plain objects (not JSON.stringify'd) — Shopify's cart API accepts nested
// objects as property values and the print tools already parse the resulting
// Ruby-hash-style string via parse_ruby_hash(), same as the full configurator.

// NOTE (2026-09-04): deliberately NOT importing ../assets/utils.js. The live
// theme's copy of that shared file has drifted from the tracked repo copy
// (docs/shopify-theme/claude-dev-cli/assets/utils.js) and is missing
// computeSafeCanvasDimensions() -- confirmed live on the sandbox theme while
// testing this editor. Rather than depend on a shared file that can silently
// rot out from under this page (see CLAUDE.md's theme-rotation warning),
// inline the two small helpers this editor actually needs.
const Utils = {
  getFileNameWithoutExtension(filename) {
    if (!filename) return "Image";
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
    return nameWithoutExt || filename;
  },
  // Mirrors utils.js's version: canvas allocation beyond these limits doesn't
  // throw -- it silently produces a blank canvas -- so anything about to size
  // a <canvas> to an image's native resolution should run dimensions through
  // this first.
  computeSafeCanvasDimensions(width, height) {
    const MAX_CANVAS_DIMENSION = 16384;
    const MAX_CANVAS_AREA = 268435456; // 16384 * 16384
    if (width <= MAX_CANVAS_DIMENSION && height <= MAX_CANVAS_DIMENSION && width * height <= MAX_CANVAS_AREA) {
      return { width, height, scale: 1, wasCapped: false };
    }
    let scale = 1;
    if (width > MAX_CANVAS_DIMENSION || height > MAX_CANVAS_DIMENSION) {
      scale = Math.min(MAX_CANVAS_DIMENSION / width, MAX_CANVAS_DIMENSION / height) * 0.9;
    }
    if (width * height * scale * scale > MAX_CANVAS_AREA) {
      scale = Math.min(scale, Math.sqrt(MAX_CANVAS_AREA / (width * height)) * 0.9);
    }
    return { width: Math.floor(width * scale), height: Math.floor(height * scale), scale, wasCapped: true };
  },
};

const MIN_SAMPLE_INCHES = 24;

if (!customElements.get("custom-sample-editor")) {
  class CustomSampleEditor extends HTMLElement {
    constructor() {
      super();

      this.config = {
        minFileSize: 50 * 1024, // 50KB
        maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB
        allowedFileTypes: [
          "image/jpeg", "image/jpg", "image/png", "image/webp", "image/tiff", "image/tif",
          "image/heic", "image/heif", "application/pdf",
        ],
        allowedFileExtensions: [
          ".jpg", ".jpeg", ".png", ".webp", ".avif", ".gif", ".bmp", ".tiff", ".tif",
          ".heic", ".heif", ".dng", ".pdf",
        ],
        heic2anyCDN: "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js",
        enableS3: true,
      };

      this.objectUrls = [];

      this.state = {
        originalFile: null,
        originalImageDimensions: null, // TRUE natural px, before any safety cap
        canvasSafetyScale: 1,
        cropper: null,
        dims: { wf: 0, wi: 0, hf: 0, hi: 0 },
        unit: "ftin", // "ftin" | "in" -- input UX only; stored/output dimensions are always feet+inches
        wallW: 0,
        wallH: 0,
        hasValidDimensions: false,
        editMode: "position", // "position" (Step 3) | "sample" (Step 4)
        wallCropApplied: false, // Step 3 confirmed; the only way into "sample" mode
        sample: { u: null, v: null }, // fractional offset of the square within the crop box
        options: { mirror: false, blackAndWhite: false },
        selectedMaterial: null,
        imageQuality: "No Image",
        effectivePpi: 0,
        originalImageS3Url: null,
        originalImageS3Key: null,
        previewImageS3Url: null,
        previewImageS3Key: null,
        conceptRef: "",
        guestCustomerId: null,
      };

      this.S3 = null;
      if (this.config.enableS3 && window.S3Integration) {
        this.S3 = new S3Integration();
      }
    }

    connectedCallback() {
      this.data = JSON.parse(this.querySelector("#data")?.textContent || "{}");
      this.selectElements();
      this.readConceptRef();
      this.setDefaultMaterial();
      this.attachEventListeners();
      this.updateSummary();
      this.syncSteps();
    }

    disconnectedCallback() {
      this.objectUrls.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (e) {}
      });
    }

    // ---- setup -------------------------------------------------------------

    selectElements() {
      const $ = (sel) => this.querySelector(sel);
      this.elements = {
        root: $("#custom-sample-editor"),
        uploadZone: $("#upload-zone"),
        imageUpload: $("#image-upload"),
        croppingArea: $("#cropping-area"),
        cropImage: $("#crop-image"),
        wallCropCard: $("#cse-step-wallcrop"),
        sampleCropCard: $("#cse-step-samplecrop"),
        applyWallCropBtn: $("#cse-apply-wallcrop"),
        wallCropDone: $("#cse-wallcrop-done"),
        editWallCropBtn: $("#cse-edit-wallcrop"),
        mirrorBtn: $("#mirror-btn"),
        bwBtn: $("#bw-btn"),
        clearImageBtn: $("#clear-image"),
        hint: $("#cse-hint"),
        qualityDPI: $("#quality-dpi"),
        qualityScore: $("#quality-score"),
        widthFeet: $("#width-feet"),
        widthInches: $("#width-inches"),
        heightFeet: $("#height-feet"),
        heightInches: $("#height-inches"),
        widthInchesTotal: $("#width-inches-total"),
        heightInchesTotal: $("#height-inches-total"),
        unitFtInBtn: $("#cse-unit-ftin"),
        unitInBtn: $("#cse-unit-in"),
        materialList: $("#material-list"),
        summaryMaterial: $("#summary-material"),
        summaryWallsize: $("#summary-wallsize"),
        summaryImage: $("#summary-image"),
        addToCartBtn: $("#add-to-cart-button"),
        errorEl: $("#cse-error"),
      };
    }

    readConceptRef() {
      try {
        const params = new URLSearchParams(window.location.search);
        this.state.conceptRef = params.get("ref") || "";
      } catch (e) {}
      try {
        this.state.guestCustomerId = sessionStorage.getItem("guest_customer_id");
      } catch (e) {}
    }

    setDefaultMaterial() {
      const first = this.elements.materialList?.querySelector(".material-item");
      if (first) this.state.selectedMaterial = first.dataset.material;
    }

    attachEventListeners() {
      this.elements.imageUpload?.addEventListener("change", (e) => this.handleImageUpload(e));
      this.elements.uploadZone?.addEventListener("dragover", (e) => { e.preventDefault(); this.elements.uploadZone.classList.add("is-dragover"); });
      this.elements.uploadZone?.addEventListener("dragleave", () => this.elements.uploadZone.classList.remove("is-dragover"));
      this.elements.uploadZone?.addEventListener("drop", (e) => {
        e.preventDefault();
        this.elements.uploadZone.classList.remove("is-dragover");
        const file = e.dataTransfer?.files?.[0];
        if (file) this.acceptFileFromInput(file);
      });

      this.elements.clearImageBtn?.addEventListener("click", () => this.removeImage());
      this.elements.mirrorBtn?.addEventListener("click", () => this.toggleOption("mirror"));
      this.elements.bwBtn?.addEventListener("click", () => this.toggleOption("blackAndWhite"));

      this.elements.applyWallCropBtn?.addEventListener("click", () => this.applyWallCrop());
      this.elements.editWallCropBtn?.addEventListener("click", () => this.editWallCrop());

      this.elements.unitFtInBtn?.addEventListener("click", () => this.setUnit("ftin"));
      this.elements.unitInBtn?.addEventListener("click", () => this.setUnit("in"));

      ["widthFeet", "widthInches", "heightFeet", "heightInches", "widthInchesTotal", "heightInchesTotal"].forEach((key) => {
        this.elements[key]?.addEventListener("input", () => this.onDimensionsInput());
      });

      this.elements.materialList?.querySelectorAll(".material-item").forEach((btn) => {
        btn.addEventListener("click", () => this.selectMaterial(btn));
      });

      this.elements.addToCartBtn?.addEventListener("click", () => this.addToCart());

      window.addEventListener("resize", () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this.layoutSampleBox(), 150);
      });
    }

    // ---- dimensions ----------------------------------------------------------

    // Ft+In / Inches is an input-UX choice only. Internally, and in every
    // stored/output value (formatDimensions, line-item properties), wall
    // dimensions are always feet+inches -- Ross's standing rule.
    setUnit(unit) {
      if (this.state.unit === unit) return;

      // Carry the current value across when switching, so nothing resets.
      if (unit === "in") {
        const totalW = (this.state.dims.wf * 12 + this.state.dims.wi).toFixed(1);
        const totalH = (this.state.dims.hf * 12 + this.state.dims.hi).toFixed(1);
        if (this.elements.widthInchesTotal) this.elements.widthInchesTotal.value = totalW;
        if (this.elements.heightInchesTotal) this.elements.heightInchesTotal.value = totalH;
      } else {
        const totalW = this.clampNum(this.elements.widthInchesTotal?.value, 0, 12000);
        const totalH = this.clampNum(this.elements.heightInchesTotal?.value, 0, 12000);
        if (this.elements.widthFeet) this.elements.widthFeet.value = Math.floor(totalW / 12);
        if (this.elements.widthInches) this.elements.widthInches.value = (totalW % 12).toFixed(1);
        if (this.elements.heightFeet) this.elements.heightFeet.value = Math.floor(totalH / 12);
        if (this.elements.heightInches) this.elements.heightInches.value = (totalH % 12).toFixed(1);
      }

      this.state.unit = unit;
      const ftinActive = unit === "ftin";
      this.elements.unitFtInBtn?.classList.toggle("is-active", ftinActive);
      this.elements.unitFtInBtn?.setAttribute("aria-checked", ftinActive ? "true" : "false");
      this.elements.unitInBtn?.classList.toggle("is-active", !ftinActive);
      this.elements.unitInBtn?.setAttribute("aria-checked", !ftinActive ? "true" : "false");

      this.querySelectorAll('[data-unit-group="ftin"]').forEach((el) => { el.hidden = !ftinActive; });
      this.querySelectorAll('[data-unit-group="in"]').forEach((el) => { el.hidden = ftinActive; });

      this.onDimensionsInput();
    }

    onDimensionsInput() {
      let wf, wi, hf, hi;
      if (this.state.unit === "in") {
        const totalW = this.clampNum(this.elements.widthInchesTotal?.value, 0, 12000);
        const totalH = this.clampNum(this.elements.heightInchesTotal?.value, 0, 12000);
        wf = Math.floor(totalW / 12);
        wi = totalW % 12;
        hf = Math.floor(totalH / 12);
        hi = totalH % 12;
      } else {
        wf = this.clampNum(this.elements.widthFeet.value, 0, 999);
        wi = this.clampNum(this.elements.widthInches.value, 0, 11.9);
        hf = this.clampNum(this.elements.heightFeet.value, 0, 999);
        hi = this.clampNum(this.elements.heightInches.value, 0, 11.9);
      }
      this.state.dims = { wf, wi, hf, hi };

      const wallW = wf * 12 + wi;
      const wallH = hf * 12 + hi;
      const changed = wallW !== this.state.wallW || wallH !== this.state.wallH;
      this.state.wallW = wallW;
      this.state.wallH = wallH;
      this.state.hasValidDimensions = wallW >= MIN_SAMPLE_INCHES && wallH >= MIN_SAMPLE_INCHES;

      if (this.state.cropper && changed) {
        // The wall frame changed shape, so an applied wall crop is stale —
        // drop back to Step 3 before Cropper re-reads editMode for dragMode.
        this.resetWallCrop();
        this.updateCropperAspectRatio();
        // re-center the sample square whenever the wall size changes
        this.state.sample = { u: null, v: null };
      }
      this.invalidatePreviewUpload();
      this.updateHint();
      this.updateQuality();
      this.updateSummary();
      this.syncSteps();
      this.validateForm();
      this.layoutSampleBox();
    }

    clampNum(v, min, max) {
      const n = parseFloat(v);
      if (isNaN(n)) return 0;
      return Math.min(max, Math.max(min, n));
    }

    formatDimensions() {
      const { wf, wi, hf, hi } = this.state.dims;
      const widthStr = wf > 0 ? `${wf}'${wi.toFixed(1)}"` : `${wi.toFixed(1)}"`;
      const heightStr = hf > 0 ? `${hf}'${hi.toFixed(1)}"` : `${hi.toFixed(1)}"`;
      return `${widthStr} × ${heightStr}`;
    }

    updateHint() {
      if (!this.elements.hint) return;
      if (!this.state.originalFile) {
        this.elements.hint.textContent = "Upload a photo to get started.";
      } else if (!this.state.hasValidDimensions) {
        this.elements.hint.textContent = "Enter your wall size (at least 2' × 2') to place your 24\" × 24\" sample.";
      } else if (this.state.editMode === "position") {
        // Position mode is the default and Step 4's own helper-text already
        // explains switching modes — an extra line here just duplicated it
        // and crowded the space above the Need Help card (Ross, 2026-09-04).
        this.elements.hint.textContent = "";
      } else {
        this.elements.hint.textContent = "Drag the square to choose which 24\" × 24\" section of your photo prints.";
      }
    }

    // ---- material --------------------------------------------------------

    selectMaterial(btn) {
      this.state.selectedMaterial = btn.dataset.material;
      this.elements.materialList.querySelectorAll(".material-item").forEach((b) => {
        const active = b === btn;
        b.classList.toggle("active", active);
        b.setAttribute("aria-selected", active ? "true" : "false");
      });
      this.updateSummary();
      this.validateForm();
    }

    // ---- mirror / b&w ------------------------------------------------------

    toggleOption(option) {
      this.state.options[option] = !this.state.options[option];
      const btn = option === "mirror" ? this.elements.mirrorBtn : this.elements.bwBtn;
      btn?.classList.toggle("active", this.state.options[option]);
      this.applyImageTransformations();
      this.invalidatePreviewUpload();
    }

    applyImageTransformations() {
      const filters = this.state.options.blackAndWhite ? "grayscale(100%)" : "";
      if (this.elements.cropImage) this.elements.cropImage.style.filter = filters;
      this.state.cropper?.scale(this.state.options.mirror ? -1 : 1, 1);
    }

    // ---- edit mode / steps ---------------------------------------------------

    setEditMode(mode) {
      this.state.editMode = mode;
      this.elements.root.dataset.editMode = mode;
      if (this.state.cropper) {
        this.state.cropper.setDragMode(mode === "position" && this.state.hasValidDimensions ? "move" : "none");
      }
      this.updateHint();
    }

    applyWallCrop() {
      if (!this.state.originalFile || !this.state.hasValidDimensions) return;
      this.state.wallCropApplied = true;
      this.setEditMode("sample");
      this.syncSteps();
      this.validateForm();
      this.layoutSampleBox();
    }

    editWallCrop() {
      this.resetWallCrop();
      this.syncSteps();
      this.validateForm();
    }

    resetWallCrop() {
      this.state.wallCropApplied = false;
      this.setEditMode("position");
    }

    // Step 3 is locked until there's a photo and a valid wall size, done once
    // applied; Step 4 opens only while Step 3 is done. Steps 2 and 5 are never
    // gated (Ross, 2026-09-04).
    syncSteps() {
      const canFrame = !!this.state.originalFile && this.state.hasValidDimensions;
      const wallState = !canFrame ? "locked" : this.state.wallCropApplied ? "done" : "active";
      const sampleState = wallState === "done" ? "active" : "locked";

      if (this.elements.wallCropCard) this.elements.wallCropCard.dataset.stepState = wallState;
      if (this.elements.sampleCropCard) this.elements.sampleCropCard.dataset.stepState = sampleState;
      if (this.elements.applyWallCropBtn) {
        this.elements.applyWallCropBtn.disabled = wallState !== "active";
        this.elements.applyWallCropBtn.hidden = wallState === "done";
      }
      if (this.elements.wallCropDone) this.elements.wallCropDone.hidden = wallState !== "done";
    }

    // ---- upload pipeline (copied from wallpaper-configurator.js) -----------

    async handleImageUpload(e) {
      const rawFile = e.target.files[0];
      await this.acceptFileFromInput(rawFile);
      e.target.value = "";
    }

    async acceptFileFromInput(rawFile) {
      const file = await this.acceptIncomingFile(rawFile);
      if (file) {
        this.state.originalFile = file;
        this.state.originalImageS3Key = null;
        this.state.originalImageS3Url = null;
        this.invalidatePreviewUpload();
        this.resetWallCrop(); // a new photo always starts at Step 3
        await this.showCroppingInterface(file);
        this.elements.summaryImage.textContent = Utils.getFileNameWithoutExtension(file.name) || "Image";
        this.updateHint();
        this.updateSummary();
        this.syncSteps();
        this.validateForm();
      }
    }

    validateFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      const byMime = this.config.allowedFileTypes.includes(type);
      const byExt = this.config.allowedFileExtensions.some((ext) => name.endsWith(ext));
      const byImagePrefix = type.startsWith("image/");
      const byPdf = type === "application/pdf";
      if (!byMime && !byExt && !byImagePrefix && !byPdf) {
        alert("Please upload a JPG, PNG, HEIC, WebP, TIFF, or PDF file.");
        return false;
      }
      if (file.size < this.config.minFileSize) {
        alert("File size must be at least 50KB.");
        return false;
      }
      if (file.size > this.config.maxFileSize) {
        alert("File size must be under 1GB.");
        return false;
      }
      return true;
    }

    isHEICFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      return type === "image/heic" || type === "image/heif" || /\.(heic|heif)$/.test(name);
    }

    isDNGFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      return type === "image/x-adobe-dng" || type === "image/dng" || /\.dng$/.test(name);
    }

    isVideoFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      return type.startsWith("video/") || /\.(mov|mp4|m4v|avi|mkv|webm)$/.test(name);
    }

    isPDFFile(file) {
      return (file.type || "").toLowerCase() === "application/pdf" || /\.pdf$/i.test(file.name || "");
    }

    isTIFFFile(file) {
      const type = (file.type || "").toLowerCase();
      return type === "image/tiff" || type === "image/tif" || /\.(tiff|tif)$/i.test(file.name || "");
    }

    async extractDNGPreview(file) {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const jpegs = [];
      let i = 0;
      while (i < bytes.length - 3) {
        if (bytes[i] === 0xff && bytes[i + 1] === 0xd8 && bytes[i + 2] === 0xff) {
          const start = i;
          let found = false;
          for (let j = start + 2; j < bytes.length - 1; j++) {
            if (bytes[j] === 0xff && bytes[j + 1] === 0xd9) {
              jpegs.push({ start, end: j + 2, size: j + 2 - start });
              i = j + 2;
              found = true;
              break;
            }
          }
          if (!found) break;
        } else {
          i++;
        }
      }
      if (jpegs.length === 0) throw new Error("No JPEG preview found inside DNG file");
      const largest = jpegs.reduce((a, b) => (b.size > a.size ? b : a));
      const jpegBytes = bytes.slice(largest.start, largest.end);
      const baseName = (file.name || "raw.dng").replace(/\.dng$/i, ".jpg");
      return new File([jpegBytes], baseName, { type: "image/jpeg" });
    }

    loadExternalScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[data-src="${src}"]`)) {
          if (typeof window.heic2any === "function") return resolve();
          document.querySelector(`script[data-src="${src}"]`).addEventListener("load", () => resolve(), { once: true });
          return;
        }
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.dataset.src = src;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load " + src));
        document.head.appendChild(s);
      });
    }

    async convertHEICToJPEG(file) {
      if (typeof window.heic2any !== "function") {
        await this.loadExternalScript(this.config.heic2anyCDN);
      }
      const result = await window.heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
      const outBlob = Array.isArray(result) ? result[0] : result;
      const newName = (file.name || "photo.heic").replace(/\.(heic|heif)$/i, ".jpg");
      return new File([outBlob], newName, { type: "image/jpeg" });
    }

    async acceptIncomingFile(rawFile) {
      if (!rawFile) return null;
      if (this.isVideoFile(rawFile)) {
        alert("We can't print video files as wallpaper. Please upload a photo or image instead.");
        return null;
      }
      if (!this.validateFile(rawFile)) return null;
      if (this.isDNGFile(rawFile)) {
        try {
          return await this.extractDNGPreview(rawFile);
        } catch (err) {
          console.error("DNG extraction failed:", err);
          alert("We couldn't read that RAW/ProRAW file. Please export it as JPEG and upload again.");
          return null;
        }
      }
      if (this.isHEICFile(rawFile)) {
        try {
          return await this.convertHEICToJPEG(rawFile);
        } catch (err) {
          console.error("HEIC conversion failed:", err);
          alert("We couldn't convert that HEIC photo. Try exporting it as JPEG and upload again.");
          return null;
        }
      }
      return rawFile;
    }

    async createCanvasForPDF(file) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      const MAX_CANVAS_DIMENSION = 16384;
      const MAX_CANVAS_AREA = 268435456;
      const initialViewport = page.getViewport({ scale: 1.0 });
      let scale = 2.0;
      let viewport = page.getViewport({ scale });
      if (viewport.width > MAX_CANVAS_DIMENSION || viewport.height > MAX_CANVAS_DIMENSION) {
        scale = Math.min(MAX_CANVAS_DIMENSION / initialViewport.width, MAX_CANVAS_DIMENSION / initialViewport.height) * 0.9;
        viewport = page.getViewport({ scale });
      }
      if (viewport.width * viewport.height > MAX_CANVAS_AREA) {
        scale = Math.sqrt(MAX_CANVAS_AREA / (initialViewport.width * initialViewport.height)) * 0.9;
        viewport = page.getViewport({ scale });
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext("2d");
      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL("image/png");
    }

    async createCanvasForTIFF(file) {
      if (typeof UTIF === "undefined") throw new Error("UTIF library not loaded. Please refresh the page.");
      const arrayBuffer = await file.arrayBuffer();
      const ifds = UTIF.decode(arrayBuffer);
      if (!ifds || ifds.length === 0) throw new Error("No images found in TIFF file");
      const ifd = ifds[0];
      UTIF.decodeImage(arrayBuffer, ifd);
      const rgba = UTIF.toRGBA8(ifd);
      const canvas = document.createElement("canvas");
      canvas.width = ifd.width;
      canvas.height = ifd.height;
      const ctx = canvas.getContext("2d");
      const imageData = ctx.createImageData(ifd.width, ifd.height);
      imageData.data.set(rgba);
      ctx.putImageData(imageData, 0, 0);
      return canvas.toDataURL("image/png");
    }

    // ---- cropper -------------------------------------------------------------

    async showCroppingInterface(file) {
      this.elements.uploadZone.style.display = "none";
      this.elements.croppingArea.style.display = "grid";

      let url;
      try {
        if (this.isPDFFile(file)) {
          url = await this.createCanvasForPDF(file);
        } else if (this.isTIFFFile(file)) {
          url = await this.createCanvasForTIFF(file);
        } else {
          url = URL.createObjectURL(file);
          this.objectUrls.push(url);
        }
      } catch (err) {
        console.error("Failed to prepare file for display:", err);
        alert("We couldn't open that file for preview. Please try a JPG or PNG instead.");
        this.removeImage();
        return;
      }

      return new Promise((resolve) => {
        this.elements.cropImage.onload = () => {
          const trueWidth = this.elements.cropImage.naturalWidth;
          const trueHeight = this.elements.cropImage.naturalHeight;
          this.state.originalImageDimensions = { width: trueWidth, height: trueHeight };

          const safe = Utils.computeSafeCanvasDimensions(trueWidth, trueHeight);
          this.state.canvasSafetyScale = safe.scale;

          if (!safe.wasCapped) {
            this.initCropper();
            resolve();
            return;
          }

          const offscreen = document.createElement("canvas");
          offscreen.width = safe.width;
          offscreen.height = safe.height;
          offscreen.getContext("2d").drawImage(this.elements.cropImage, 0, 0, safe.width, safe.height);
          offscreen.toBlob((blob) => {
            const safeUrl = URL.createObjectURL(blob);
            this.objectUrls.push(safeUrl);
            this.elements.cropImage.onload = () => {
              this.initCropper();
              resolve();
            };
            this.elements.cropImage.src = safeUrl;
          }, "image/png");
        };
        this.elements.cropImage.src = url;
      });
    }

    initCropper() {
      if (this.state.cropper) {
        this.state.cropper.destroy();
        this.state.cropper = null;
      }
      this.removeSampleOverlay();

      const { wallW, wallH, hasValidDimensions } = this.state;
      const img = this.elements.cropImage;
      let aspectRatio;
      if (hasValidDimensions) {
        aspectRatio = wallW / wallH;
      } else if (img.naturalWidth && img.naturalHeight) {
        aspectRatio = img.naturalWidth / img.naturalHeight;
      } else {
        aspectRatio = NaN;
      }

      this.state.cropper = new Cropper(img, {
        aspectRatio,
        viewMode: 1,
        dragMode: hasValidDimensions && this.state.editMode === "position" ? "move" : "none",
        autoCropArea: 1.0,
        responsive: true,
        restore: false,
        modal: true, // greys everything outside the wall frame
        guides: false,
        center: false,
        highlight: false,
        background: false,
        movable: true,
        cropBoxMovable: false,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,
        zoomable: false,
        zoomOnWheel: false,
        zoomOnTouch: false,
        wheelZoomRatio: 0,
        scalable: true, // retained so Mirror can flip the underlying image
        rotatable: false,
        minContainerWidth: 200,
        minContainerHeight: 200,
        minCropBoxWidth: 60,
        minCropBoxHeight: 60,
        ready: () => {
          if (hasValidDimensions) this.state.cropper.crop();
          this.applyImageTransformations();
          this.ensureSampleOverlay();
          this.layoutSampleBox();
          this.updateQuality();
          img.addEventListener("crop", () => {
            if (this._cropRaf) return;
            this._cropRaf = requestAnimationFrame(() => {
              this._cropRaf = null;
              this.layoutSampleBox();
            });
          });
        },
      });
    }

    updateCropperAspectRatio() {
      if (!this.state.cropper) return;
      const { wallW, wallH } = this.state;
      if (wallW > 0 && wallH > 0) {
        this.state.cropper.setAspectRatio(wallW / wallH);
        this.state.cropper.reset();
        this.state.cropper.crop();
      } else {
        this.state.cropper.setAspectRatio(NaN);
      }
      this.state.cropper.setDragMode(this.state.hasValidDimensions && this.state.editMode === "position" ? "move" : "none");
    }

    // ---- sample-area overlay -------------------------------------------------

    ensureSampleOverlay() {
      const container = this.elements.croppingArea.querySelector(".cropper-container");
      if (!container) return;
      if (this._shadeEl && this._shadeEl.isConnected) return;

      const shade = document.createElement("div");
      shade.className = "cse-sample-shade";
      const box = document.createElement("div");
      box.className = "cse-sample-box";
      const label = document.createElement("span");
      label.className = "cse-sample-box__label";
      label.textContent = '24" × 24" sample';
      box.appendChild(label);
      shade.appendChild(box);
      container.appendChild(shade);

      this._shadeEl = shade;
      this._boxEl = box;

      box.addEventListener("pointerdown", (e) => this.startBoxDrag(e));
    }

    removeSampleOverlay() {
      this._shadeEl?.remove();
      this._shadeEl = null;
      this._boxEl = null;
    }

    layoutSampleBox() {
      if (!this.state.cropper || !this._shadeEl) return;
      const { wallW, wallH, hasValidDimensions } = this.state;
      if (!hasValidDimensions) {
        this._shadeEl.style.display = "none";
        return;
      }
      this._shadeEl.style.display = "block";

      const cb = this.state.cropper.getCropBoxData();
      this._shadeEl.style.left = `${cb.left}px`;
      this._shadeEl.style.top = `${cb.top}px`;
      this._shadeEl.style.width = `${cb.width}px`;
      this._shadeEl.style.height = `${cb.height}px`;

      const maxU = Math.max(0, 1 - MIN_SAMPLE_INCHES / wallW);
      const maxV = Math.max(0, 1 - MIN_SAMPLE_INCHES / wallH);
      if (this.state.sample.u === null) this.state.sample.u = maxU / 2;
      if (this.state.sample.v === null) this.state.sample.v = maxV / 2;
      this.state.sample.u = Math.min(maxU, Math.max(0, this.state.sample.u));
      this.state.sample.v = Math.min(maxV, Math.max(0, this.state.sample.v));

      const side = cb.width * (MIN_SAMPLE_INCHES / wallW); // == cb.height * 24/wallH
      this._boxEl.style.left = `${this.state.sample.u * cb.width}px`;
      this._boxEl.style.top = `${this.state.sample.v * cb.height}px`;
      this._boxEl.style.width = `${side}px`;
      this._boxEl.style.height = `${side}px`;

      this.updateQuality();
    }

    startBoxDrag(e) {
      if (this.state.editMode !== "sample") return;
      e.preventDefault();
      const cb = this.state.cropper.getCropBoxData();
      const startX = e.clientX;
      const startY = e.clientY;
      const startU = this.state.sample.u;
      const startV = this.state.sample.v;
      const maxU = Math.max(0, 1 - MIN_SAMPLE_INCHES / this.state.wallW);
      const maxV = Math.max(0, 1 - MIN_SAMPLE_INCHES / this.state.wallH);

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        this.state.sample.u = Math.min(maxU, Math.max(0, startU + dx / cb.width));
        this.state.sample.v = Math.min(maxV, Math.max(0, startV + dy / cb.height));
        const side = cb.width * (MIN_SAMPLE_INCHES / this.state.wallW);
        this._boxEl.style.left = `${this.state.sample.u * cb.width}px`;
        this._boxEl.style.top = `${this.state.sample.v * cb.height}px`;
        this._boxEl.style.width = `${side}px`;
        this._boxEl.style.height = `${side}px`;
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        this.invalidatePreviewUpload();
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp, { once: true });
    }

    // ---- quality -------------------------------------------------------------

    // Working-image-space crop of the wall frame (Cropper's own coordinate
    // system) and the 24"x24" square inside it, both in and out of true
    // (pre-safety-cap) pixels.
    computeCropData() {
      const wall = this.state.cropper.getData(); // {x,y,width,height,rotate,scaleX,scaleY} in working px
      const k = 1 / (this.state.canvasSafetyScale || 1);
      const { u, v } = this.state.sample;
      const { wallW, wallH } = this.state;

      const sqWorking = {
        x: wall.x + u * wall.width,
        y: wall.y + v * wall.height,
        width: (wall.width * MIN_SAMPLE_INCHES) / wallW,
        height: (wall.height * MIN_SAMPLE_INCHES) / wallH,
      };

      const wallT = {
        x: wall.x * k, y: wall.y * k, width: wall.width * k, height: wall.height * k,
        rotate: 0, scaleX: wall.scaleX, scaleY: wall.scaleY,
      };
      const sqT = {
        x: wallT.x + u * wallT.width,
        y: wallT.y + v * wallT.height,
        width: (wallT.width * MIN_SAMPLE_INCHES) / wallW,
        height: (wallT.height * MIN_SAMPLE_INCHES) / wallH,
        rotate: 0, scaleX: wall.scaleX, scaleY: wall.scaleY,
      };
      return { wall, wallT, sqWorking, sqT };
    }

    updateQuality() {
      if (!this.state.cropper || !this.state.hasValidDimensions) {
        this.state.imageQuality = "No Image";
        this.state.effectivePpi = 0;
        this.updateQualityDisplay();
        return;
      }
      const { sqT } = this.computeCropData();
      const ppi = sqT.width / MIN_SAMPLE_INCHES;
      this.state.effectivePpi = ppi;
      if (ppi >= 300) this.state.imageQuality = "Excellent";
      else if (ppi >= 150) this.state.imageQuality = "Good";
      else if (ppi >= 72) this.state.imageQuality = "Fair";
      else this.state.imageQuality = "Poor";
      this.updateQualityDisplay();
    }

    updateQualityDisplay() {
      if (this.elements.qualityDPI) this.elements.qualityDPI.textContent = Math.round(this.state.effectivePpi) || "0";
      if (this.elements.qualityScore) {
        this.elements.qualityScore.textContent = this.state.imageQuality;
        this.elements.qualityScore.className = "quality-value";
        if (this.state.imageQuality !== "No Image") {
          this.elements.qualityScore.classList.add(this.state.imageQuality.toLowerCase());
        }
      }
    }

    // ---- preview image (for _Preview_Image_S3_URL) ---------------------------

    // Mirrors the FULL working image first (matching the print tool's
    // "flip first, then crop at x as-is" convention), then crops to the
    // 24x24 square in working-image pixel space.
    async generateSamplePreview() {
      const { sqWorking } = this.computeCropData();
      const img = this.elements.cropImage;
      const naturalW = img.naturalWidth;
      const naturalH = img.naturalHeight;
      const scale = Math.min(1, 2000 / Math.max(naturalW, naturalH));

      const full = document.createElement("canvas");
      full.width = Math.max(1, Math.round(naturalW * scale));
      full.height = Math.max(1, Math.round(naturalH * scale));
      const fullCtx = full.getContext("2d");
      if (this.state.options.mirror) {
        fullCtx.translate(full.width, 0);
        fullCtx.scale(-1, 1);
      }
      if (this.state.options.blackAndWhite && "filter" in fullCtx) {
        fullCtx.filter = "grayscale(100%)";
      }
      fullCtx.drawImage(img, 0, 0, full.width, full.height);

      const outSide = Math.min(1500, Math.max(1, Math.round(sqWorking.width * scale)));
      const crop = document.createElement("canvas");
      crop.width = outSide;
      crop.height = outSide;
      crop.getContext("2d").drawImage(
        full,
        sqWorking.x * scale, sqWorking.y * scale, sqWorking.width * scale, sqWorking.height * scale,
        0, 0, outSide, outSide
      );

      return new Promise((resolve) => {
        crop.toBlob((blob) => {
          resolve(new File([blob], "sample-preview.png", { type: "image/png" }));
        }, "image/png");
      });
    }

    // ---- summary / validation -------------------------------------------------

    updateSummary() {
      if (this.elements.summaryMaterial) this.elements.summaryMaterial.textContent = this.state.selectedMaterial || "Select a Material";
      if (this.elements.summaryWallsize) {
        this.elements.summaryWallsize.textContent = this.state.wallW > 0 || this.state.wallH > 0 ? this.formatDimensions() : '0.0" × 0.0"';
      }
    }

    validateForm() {
      const ready = !!this.state.originalFile && this.state.hasValidDimensions && this.state.wallCropApplied && !!this.state.selectedMaterial;
      if (this.elements.addToCartBtn) this.elements.addToCartBtn.disabled = !ready;
    }

    invalidatePreviewUpload() {
      this.state.previewImageS3Key = null;
      this.state.previewImageS3Url = null;
    }

    removeImage() {
      this.state.cropper?.destroy();
      this.state.cropper = null;
      this.removeSampleOverlay();
      this.state.originalFile = null;
      this.state.originalImageDimensions = null;
      this.state.originalImageS3Key = null;
      this.state.originalImageS3Url = null;
      this.invalidatePreviewUpload();
      this.state.sample = { u: null, v: null };
      this.resetWallCrop();
      this.elements.croppingArea.style.display = "none";
      this.elements.uploadZone.style.display = "";
      this.elements.summaryImage.textContent = "No Image";
      this.updateHint();
      this.updateQuality();
      this.updateSummary();
      this.syncSteps();
      this.validateForm();
    }

    showError(msg) {
      if (!this.elements.errorEl) return;
      this.elements.errorEl.textContent = msg || "";
      this.elements.errorEl.hidden = !msg;
    }

    // ---- add to cart -----------------------------------------------------

    validateBeforeCart() {
      if (!this.state.originalFile) { this.showError("Please upload a photo."); return false; }
      if (!this.state.hasValidDimensions) { this.showError("Please enter your wall dimensions (minimum 2' × 2')."); return false; }
      if (!this.state.wallCropApplied) { this.showError("Please apply your wall crop (Step 3) first."); return false; }
      if (!this.state.selectedMaterial) { this.showError("Please select a material."); return false; }
      this.showError("");
      return true;
    }

    async addToCart() {
      if (!this.validateBeforeCart()) return;

      this.elements.addToCartBtn.disabled = true;
      this.elements.addToCartBtn.textContent = "Processing...";

      if (this.S3 && (!this.state.originalImageS3Key || !this.state.previewImageS3Key)) {
        this.elements.addToCartBtn.textContent = "Uploading image...";

        if (!this.state.originalImageS3Key) {
          try {
            const uploadResult = await this.S3.uploadFile(this.state.originalFile, {
              productId: this.data.product?.id,
              dimensions: this.formatDimensions(),
              mirror: this.state.options.mirror,
              blackAndWhite: this.state.options.blackAndWhite,
            });
            this.state.originalImageS3Url = await this.S3.getDownloadUrl(uploadResult?.key);
            this.state.originalImageS3Key = uploadResult?.key;
          } catch (error) {
            console.error("Original image upload failed:", error);
          }
        }

        if (!this.state.previewImageS3Key) {
          try {
            const previewFile = await this.generateSamplePreview();
            const uploadResult = await this.S3.uploadFile(previewFile, {
              productId: this.data.product?.id,
              dimensions: this.formatDimensions(),
            });
            this.state.previewImageS3Url = await this.S3.getDownloadUrl(uploadResult?.key);
            this.state.previewImageS3Key = uploadResult?.key;
          } catch (error) {
            console.error("Preview image upload failed:", error);
          }
        }
      }

      // HARD GATE (mirrors order #1218 fix in wallpaper-configurator.js): never
      // let this into the cart without its artwork safely uploaded.
      if (!this.state.originalImageS3Key || !this.state.previewImageS3Key) {
        this.elements.addToCartBtn.disabled = false;
        this.elements.addToCartBtn.textContent = "Upload failed - click to retry";
        this.showError("We couldn't finish uploading your image. Nothing was added to your cart. Please check your connection and try again.");
        return;
      }

      const { sqT } = this.computeCropData();

      const properties = {
        "Image Name": this.state.originalFile.name,
        "Image Mode": "Custom Sample",
        "Material": this.state.selectedMaterial,
        "Sample Size": '24" × 24"',
        "Dimensions": this.formatDimensions(),
        "Black & White": this.state.options.blackAndWhite ? "Yes" : "No",
        "Mirror": this.state.options.mirror ? "Yes" : "No",
        "_Sample_Size_Inches": MIN_SAMPLE_INCHES,
        "_Crop_Data": sqT,
        "_Wall_Crop_Data": this.computeCropData().wallT,
        "_Sample_Offset": { u: this.state.sample.u, v: this.state.sample.v },
        "_Crop X": Math.round(sqT.x),
        "_Crop Y": Math.round(sqT.y),
        "_Crop Width": Math.round(sqT.width),
        "_Crop Height": Math.round(sqT.height),
        "_Crop Rotate": 0,
        "_Crop ScaleX": sqT.scaleX || 1,
        "_Crop ScaleY": sqT.scaleY || 1,
        "_Original Image Width": this.state.originalImageDimensions?.width || 0,
        "_Original Image Height": this.state.originalImageDimensions?.height || 0,
        "_Original Aspect Ratio": this.state.originalImageDimensions
          ? (this.state.originalImageDimensions.width / this.state.originalImageDimensions.height).toFixed(3)
          : "1.000",
        "_Effective_PPI": Math.round(this.state.effectivePpi),
        "_DPI_Assessment": this.state.imageQuality,
        "_Original_File_Type": this.state.originalFile.type || "unknown",
        "_Original_Image_URL": this.state.originalImageS3Url || "N/A",
        "_Original_Image_S3_Key": this.state.originalImageS3Key || "N/A",
        "_Preview_Image_S3_URL": this.state.previewImageS3Url || "N/A",
        "_Preview_Image_S3_Key": this.state.previewImageS3Key || "N/A",
        "_Is_Pre_Existing": "No",
        "_Pre_Existing_URL": "N/A",
      };

      if (this.state.conceptRef) properties["Concept Reference"] = this.state.conceptRef;
      if (this.state.guestCustomerId) properties["_guest_customer_id"] = this.state.guestCustomerId;

      const cartData = {
        id: this.data.variantId,
        quantity: 1,
        properties,
      };

      this.elements.addToCartBtn.textContent = "Adding to cart...";

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cartData),
        });
        if (response.ok) {
          window.location.href = "/cart";
        } else {
          throw new Error("Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        this.elements.addToCartBtn.disabled = false;
        this.elements.addToCartBtn.textContent = "Add To Cart";
        this.showError("Something went wrong adding this to your cart. Please try again.");
      }
    }
  }

  customElements.define("custom-sample-editor", CustomSampleEditor);
}
